import {Spec} from 'immutability-helper';
import {UpdateSpec} from 'react';

import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import {Selector} from 'notebook/ELNConstants';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {BenchlingSelection, SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {TableLocationNameArgs, OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {TableOpFunction, TableOpOptions} from 'notebook/tables/excess/ops/TableOpConstants';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {BasicEvent, ElementKeyboardEvent} from 'util/Types';

import {MediocreSearchResultProps} from './search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from './search/SearchResults';

// An non-comprehensive set of actions supported in mediocre editor. This is currently limited to the subset
// of actions that a plugin may want to block via its `this.getReasonForDisallowedAction` implementation.
export enum MediocreItemAction {
  DELETE = 'DELETE',
}

export interface ItemInsertOptions {
  insertAtBoundaryStartsNextDay?: boolean;
}

export interface ReplaceItemsOptions extends ItemInsertOptions {
  onComplete?: () => void;
}

export interface PendingCut {
  cid: string;
  provenanceUuid?: string;
}

/**
 * Minimal plugin type for what MediocreItem and MediocreItemComponent actually need.
 * This avoids the circular type dependency between plugins and editors.
 */
export interface MediocreItemPlugin<TItem> {
  typeClass?: string;
  component: {
    make: (
      item: TItem,
      index: number,
      editorProps: EditorProps<TItem>,
      itemProps: unknown,
      searchResultProps: MediocreSearchResultProps
    ) => JSX.Element;
    getVirtualizedHeight?: (item: TItem, isCollapsed: boolean) => number;
    renderVirtualizedSkeleton?: (item: TItem, height: number) => JSX.Element;
    skipVirtualizationDueToPerformantRender?: (item: TItem) => boolean;
    getVirtualizationId?: (item: TItem) => string;
    getIsCollapsed?: (item: TItem, editorProps: EditorProps<TItem>, collapsedStateById: unknown) => boolean;
  };
}

/**
 * User object returned by getCreator, matching Timestamp component props.
 */
export interface MediocreItemCreator {
  name: string;
  email: string;
  handle: string;
  url_identifier: string;
}

/**
 * Minimal interface for the editor methods that MediocreItem directly uses.
 * This allows MediocreItem to accept a narrower type than the full Editor,
 * while still supporting the mixin-based editor pattern.
 */
export interface MediocreItemEditor<TItem = unknown> {
  itemMouseDownHandler: (index: number, e: React.MouseEvent) => void;
  itemMouseOverHandler: (index: number, e: React.MouseEvent) => void;
  itemMouseMoveHandler: (index: number, e: React.MouseEvent) => void;
  getPluginByType: (type: string) => MediocreItemPlugin<TItem>;
  getCreator?: (item: TItem) => MediocreItemCreator | undefined;
  getModifiedAt?: (item: TItem) => string | undefined;
}

export type Editor<TRoot = unknown, TItem = unknown> = {
  props: {
    getItems: (root: TRoot) => Array<TItem>;
    getType: (item: TItem) => string;
    fileId: string;
    getCid: (item: TItem) => string;
    getContent: (item: TItem) => unknown;
    usedTableNames: () => Array<string>;
    metadataForRestore: MetadataForRestore<TableIndex> | {selection: SelectionRestoreData} | null;
  };
  getPluginByType: (type: string) => MediocrePlugin<Editor<TRoot, TItem>, EditorProps<TItem>, TItem>;
  getComputedTables: (root: TRoot) => OutputTableMap | null;
  getSearchableItems: (root: TRoot) => Array<TItem>;
} & {
  // TODO(TypeScript): Fill in.
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};
export interface EditorProps<TItem> {
  fileId: string;
  locked: boolean;
  // Indicates the document is locked due to upstream changes that the client is waiting on.
  dirty?: boolean;
  // restore state from history on undo
  restoreState: (
    e: ElementKeyboardEvent,
    selectionRestoreData: MetadataForRestore<TableIndex> | {selection: SelectionRestoreData}
  ) => boolean;
  // store the state of an entry in edit history
  storeState: (
    selectionRestoreData?: MetadataForRestore<TableIndex> | {selection: SelectionRestoreData} | null
  ) => void;
  undoState: (selectionRestoreData: {selection: SelectionRestoreData}) => void;
  requestSelection: (
    selectionRestoreData?: MetadataForRestore<TableIndex> | {selection: SelectionRestoreData}
  ) => void;
  spellCheck: boolean;
  viewer: boolean;
  getCid: (item: TItem) => string;
  getProvenanceId?: (item: TItem) => number | null;
  getContent: (item: TItem) => unknown;
  setContent: (type: string, content: object) => Spec<TItem>;
  getSpreadsheetTableName: (item: TItem) => string | undefined;
  getType: (item: TItem) => string;
  performTableOp: ({
    tableName,
    op,
    options,
  }: {
    tableName: string;
    op: TableOpFunction;
    options?: TableOpOptions;
  }) => void;
  isTableNameUsed: (tableName: string) => boolean;
  tryInsert: (
    {tableName, documentName}: TableLocationNameArgs,
    e: {index: [number, number]; start: number; end: number; cid: string; shift: boolean}
  ) => boolean;
  canInsert: (e: {start: number; end: number; cid: string; shift: boolean}) => boolean;
  setMultiEditingForTable: (
    {tableName, documentName}: TableLocationNameArgs,
    {
      index,
      editing,
      text,
      typedValue,
      selection,
    }: {
      index: TableIndex | null;
      editing: boolean;
      text: string;
      typedValue: TypedValue;
      selection: {cid: string; start: number; end: number} | null;
    }
  ) => void;
  updateSelectors: (newSelectors: Array<Selector>) => void;
  usedTableNames: () => Array<string>;
  spreadsheetTypes: Array<string>;
  setPendingCuts: (cuts: Array<PendingCut>) => void;
  clearPendingCuts: () => void;
  addAIDataEntryRowsUsingAI?: (
    initialPromptText?: string,
    initialSelectedTableName?: string,
    structuredTable?: StructuredTable<unknown, unknown>,
    result?: AddStructuredTableRowsUsingAIResult | null
  ) => Promise<void>;
  // Come from the collapsible state connector.
  collapseAllTables: () => void;
  expandAllTables: () => void;
  initAllTables: () => void;
  areAllTablesExpanded: boolean;
  areAllTablesCollapsed: boolean;
}

export type MediocreSelection = MetadataForRestore<TableIndex> | {selection: BenchlingSelection};

export interface MediocrePluginComponent<TEditor, TEditorProps, TItem, TItemProps = unknown> {
  default?: (this: TEditor, ...rest: Array<unknown>) => unknown;
  selectionInit: (this: TEditor, item: TItem) => MediocreSelection | null;
  selectionAtStart: (this: TEditor, item: TItem) => MediocreSelection | null;
  selectionAtEnd: (this: TEditor, item: TItem) => MediocreSelection | null;
  selectionAll?: (item: TItem) => MediocreSelection | null;
  required: Array<string>;
  getItemProps?: (this: TEditor, item: TItem, index: number) => TItemProps;
  /**
   * The render function for the item. Note that this is _only_ re-evaluated if item/editorProps/itemProps
   * /searchResultProps change (in particular, not if this.props change). See havePropsChanged in
   * MediocreItem.js.
   */
  make: (
    this: TEditor,
    item: TItem,
    index: number,
    editorProps: TEditorProps,
    itemProps: TItemProps,
    searchResultProps: MediocreSearchResultProps
  ) => JSX.Element;
  /**
   * If the component is rendered outside the viewport and `skipVirtualizationDueToPerformantRender` is false
   * for the item, we will call `renderVirtualizedSkeleton` instead, with the height returned by
   * `getVirtualizedHeight`. Once the component enters the viewport, it will be replaced by the React element
   * specified in `make`.
   *
   * This is a best-effort height estimate to avoid jarring changes in the document height as various parts of
   * the entry are scrolled into view.
   *
   * TODO(BNCH-36331): Make these required, and consider allowing `null` if we want to support fallbacks.
   */
  getVirtualizedHeight?: (item: TItem, isCollapsed: boolean) => number;
  renderVirtualizedSkeleton?: (item: TItem, height: number) => JSX.Element;
  skipVirtualizationDueToPerformantRender?: (item: TItem) => boolean;
  /**
   * A unique stable identifier for `item`
   *  - "unique" within the set of same-type items in the document.
   *  - "stable" across data re-fetch / re-mounts - identifier can change on client modifications to the item.
   *
   * A stable identifier ensures that scroll position is restored accurately on re-mount. If the identifier
   * is not stable, any inaccuracies in `getVirtualizedHeight()` will result in scroll position jumpiness.
   */
  getVirtualizationId?: (item: TItem) => string;

  /**
   * Indicate if `item` should be rendered as collapsed based on `editorProps` and `collapsedStateById`.
   *
   * @param collapsedStateById - from redux store:
   *  state.ui.collapsedTables
   */
  getIsCollapsed?: (
    item: TItem,
    editorProps: TEditorProps,
    collapsedStateById: CollapsedTableState
  ) => boolean;
  /**
   * Indicate if the contents of this item should not be surfaced to the render.
   * Currently, `make` is expected to make a similar computation, but ideally would share logic (BNCH-43256).
   *
   * If true, search results will be skipped for this item.
   */
  getIsInaccessible?: (item: TItem) => boolean;
}

export interface StoreDependencyMap {
  [dependencyType: string]: Array<string>;
}

/**
 * Contract implemented by each Mediocre editor plugin.
 *
 * A plugin declares the metadata for a note-part type and provides conversion,
 * rendering, and editing helpers that the editor dispatches to at runtime.
 */
export interface MediocrePlugin<TEditor, TEditorProps, TItem> {
  /**
   * Produces search hits for this item type when using the Ctrl-F, in app, search
   *
   * The document search pipeline invokes this for each item of this type and passes:
   * - the query matcher
   * - `searchOverFormulas` (whether formula text should be searched)
   * - current computed table results
   * - collapsed state for the current item (when available)
   *
   * Returning a generator allows implementations to lazily yield matches.
   */
  getSearchResults?: (
    item: TItem,
    matcher: TextMatcher,
    searchOverFormulas?: boolean,
    computedTables?: OutputTableMap | null,
    isCollapsed?: boolean
  ) => Generator<MediocreSearchResult>;
  /**
   * Whether the item can collapse. May transform/change as different states are set on the note part.
   */
  isCollapsible?: (notePart: NotePart) => boolean;
  canAutoCollapse?: boolean;
  /**
   * Item type identifier for this plugin.
   *
   * This should match the note-part `type` value for items handled by this
   * plugin so type-to-plugin lookup can route behavior correctly.
   */
  type: string;
  /**
   * Optional CSS class attached to the root `.mediocre-item` wrapper.
   *
   * Use this for type-specific styling on the item container.
   */
  typeClass?: string;
  /**
   * Human-readable label for this item type.
   *
   * Consumers can use this in insertion/selection UIs, but each UI chooses
   * whether and how to surface it.
   */
  title: string;
  /**
   * Visual identifier for this item type.
   *
   * Consumers can use this in insertion/selection UIs, but each UI chooses
   * whether and how to surface it.
   */
  icon: BenchlingIconName;
  /**
   * Capability metadata indicating whether this type supports item linking.
   *
   * This is primarily declarative metadata on plugin definitions today.
   */
  linkable?: boolean;
  /**
   * Whether this item type supports left/center/right alignment.
   *
   * This is used as capability metadata when deciding whether alignment controls
   * should be enabled for a selected item.
   */
  alignable?: boolean;
  /**
   * Cross-tenant paste policy for HTML clipboard imports.
   *
   * During HTML paste/import, items from a different tenant origin are allowed
   * only when this is explicitly `true`. When this is `false` or `undefined`,
   * cross-tenant items are skipped and a warning is shown to the user.
   */
  canPasteFromOtherTenant?: boolean;
  /**
   * Rendering + selection behavior for this item type.
   *
   * The editor runtime calls these hooks for selection, item props, rendering,
   * virtualization heuristics, and if the item is accessible for Ctrl-F searches.
   */
  component: MediocrePluginComponent<TEditor, TEditorProps, TItem>;
  /**
   * Rehydrates an item from serialized HTML clipboard/import content.
   *
   * Called after base item JSON is parsed from clipboard/import HTML and
   * assigned a fresh client `cid`. Return `null` when this element should be
   * skipped during import.
   */
  itemFromHTML: (
    this: TEditor,
    $el: JQuery,
    item: TItem,
    fileAnalyticsParams?: FileAnalyticsParams
  ) => TItem | null;
  /**
   * Serializes an item into the plugin-specific HTML fragment used for copy.
   *
   * The clipboard/export flow wraps this output with shared editor metadata
   * (such as editor identity and origin).
   */
  itemToHTML: (this: TEditor, $el: JQuery, item: TItem) => JQuery;
  /**
   * Serializes an item to plain text for text-only clipboard paths.
   *
   * In text-only clipboard flows, null/undefined values are filtered out before
   * per-item lines are joined.
   */
  itemToText?: (this: TEditor, item: TItem) => string | null;
  /**
   * Returns the plugin-owned content payload from an item.
   *
   * Container/editor layers delegate to this so they can work with
   * plugin-specific content shapes through a common interface.
   */
  getContent: (this: TEditor, item: TItem) => unknown;
  /**
   * Returns an immutability-helper spec for writing plugin content into the Editor store
   *
   * Container/editor layers delegate to this, and update paths apply the
   * returned `Spec<TItem>` to produce immutable item updates which can then
   * be applied at the store level for an update of the specific item.
   */
  setContent: (this: TEditor, data: unknown) => Spec<TItem>;
  /**
   * Returns the canonical spreadsheet table name represented by this item.
   *
   * Spreadsheet-aware flows require this for table evaluation, table-specific
   * props, and table-level operations.
   */
  getSpreadsheetTableName?: (this: TEditor, item: TItem) => string;
  /**
   * Returns formatted text content for text-edit operations.
   *
   * Used by text-edit flows (for example keyboard editing and text transforms).
   */
  getText?: (this: TEditor, data: {text: string; formats: Format} | TItem) => {text: string; formats: Format};
  /**
   * Produces an update spec for formatted text writes.
   *
   * Used by text-edit flows to generate immutable updates for text content.
   */
  setText?: (this: TEditor, data: {text: string; formats: Format}) => UpdateSpec;
  /**
   * Declares store dependencies required for spreadsheet re-evaluation.
   *
   * Spreadsheet evaluation aggregates these dependencies across relevant items
   * before recomputing table outputs.
   */
  getStoreDependenciesForSpreadsheetEval?: (item: TItem) => StoreDependencyMap;
  /**
   * Returns a warning message when an item action should be blocked.
   *
   * Action-validation flows (for example delete/replace) can call this and
   * cancel the edit + show a notification when a non-null message is returned.
   */
  getReasonForDisallowedAction?: (this: TEditor, item: TItem, action: MediocreItemAction) => string | null;
  /**
   * Normalizes an imported item before it is inserted into the destination.
   *
   * Called during paste/import conversion. If implemented, this hook is used as
   * the conversion path for this item type instead of the default normalization
   * behavior.
   * Implementations typically:
   * - rewrite names to avoid collisions (tables, text boxes, etc.)
   * - regenerate per-document IDs/provenance where needed
   * - return old/new names so cross-table formula references can be remapped
   */
  convertItemForPaste?: (
    this: TEditor,
    item: TItem,
    destinationFileId: string,
    usedTableNames?: Array<string>,
    usedTextBoxNames?: Array<string>,
    isDestinationConfig?: boolean
  ) => {addedItem: NotePart; oldName: string; newName: string};
}

export interface SelectionPartition {
  before: string;
  after: string;
  selected: string;
  afterPoint: boolean;
}

export interface SplitEvent extends SelectionPartition {
  modifierKey: boolean;
  shiftKey: boolean;
  originalEvent: BasicEvent;
}

export interface PasteEvent extends SelectionPartition {
  before: string;
  after: string;
  selected: string;
}

export interface TabEvent {
  partition: SelectionPartition;
  shiftKey: boolean;
  target: EventTarget;
}

export type PerformSingleInputTableOpFunc = (index: number, startIndex: number, endIndex: number) => void;
